if (!customElements.get('related-grid')) {
  class RelatedGrid extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-rg-list]');
      this.tabs = Array.from(this.querySelectorAll('[data-rg-tab]'));
      this.items = Array.from(this.querySelectorAll('.related-grid__item'));

      this.bindTabs();
      this.bindCarousel();
      this.bindAutoplay();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.autoplayObserver?.disconnect();
      this.stopAutoplay();
      if (this.syncAutoplay) {
        document.removeEventListener('visibilitychange', this.syncAutoplay);
      }
    }

    /* Tabs */

    bindTabs() {
      if (!this.tabs.length || !this.items.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
      });
    }

    select(tab) {
      const handle = tab.dataset.rgTab;

      this.tabs.forEach((el) => {
        el.setAttribute('aria-selected', el === tab ? 'true' : 'false');
      });

      this.items.forEach((item) => {
        // An empty handle is the "All" tab.
        if (!handle) {
          item.hidden = false;
          return;
        }

        const card = item.querySelector('[data-collections]');
        const handles = (card?.dataset.collections || '').split(/\s+/);
        item.hidden = !handles.includes(handle);
      });

      // A shorter row starts over, and may not be scrollable at all any more.
      this.list?.scrollTo({ left: 0 });
      this.updateArrows();
    }

    /* Carousel
       The row itself is a scroll container styled by the section CSS, so the
       arrows only have to nudge it one view along and reflect where it sits. */

    bindCarousel() {
      if (!this.hasAttribute('data-rg-carousel') || !this.list) return;

      this.controls = this.querySelector('[data-rg-controls]');
      this.previousButton = this.querySelector('[data-rg-prev]');
      this.nextButton = this.querySelector('[data-rg-next]');

      if (!this.previousButton || !this.nextButton) return;

      this.previousButton.addEventListener('click', () => this.page(-1));
      this.nextButton.addEventListener('click', () => this.page(1));
      this.list.addEventListener('scroll', () => this.updateArrows(), { passive: true });

      // Products per view changes with the viewport, and so does whether the
      // row overflows at all.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateArrows());
        this.resizeObserver.observe(this.list);
      }

      this.updateArrows();
    }

    page(direction) {
      // No behavior, so the CSS scroll-behavior decides, reduced motion included.
      this.list.scrollBy({ left: direction * this.list.clientWidth });
    }

    /* Autoplay
       Small screens only. There the row is one product wide, so advancing it is what
       tells a shopper there is more than the first card. On wider screens several are
       already on show and a row that moves on its own is just a distraction. */

    bindAutoplay() {
      const seconds = Number(this.dataset.rgAutoplay);
      if (!this.list || !seconds) return;

      this.autoplayDelay = seconds * 1000;
      this.autoplayTimer = null;
      // Set once the shopper takes over; nothing restarts the timer after that.
      this.autoplayReleased = false;
      this.autoplayVisible = true;

      const isSmall = window.matchMedia('(max-width: 749px)');
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.syncAutoplay = () => {
        const shouldRun =
          isSmall.matches &&
          !prefersReducedMotion.matches &&
          this.autoplayVisible &&
          !document.hidden;

        if (shouldRun) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      };

      isSmall.addEventListener('change', this.syncAutoplay);
      prefersReducedMotion.addEventListener('change', this.syncAutoplay);
      document.addEventListener('visibilitychange', this.syncAutoplay);

      // A row scrolling itself off-screen is wasted work and wasted battery.
      if ('IntersectionObserver' in window) {
        this.autoplayObserver = new IntersectionObserver(
          ([entry]) => {
            this.autoplayVisible = entry.isIntersecting;
            this.syncAutoplay();
          },
          { threshold: 0.2 }
        );
        this.autoplayObserver.observe(this);
      }

      // Once someone swipes, taps an arrow or tabs into a card, stop competing with
      // them: an auto-advance mid-read moves what they were reaching for.
      ['pointerdown', 'focusin'].forEach((type) => {
        this.addEventListener(
          type,
          () => {
            this.autoplayReleased = true;
            this.stopAutoplay();
          },
          { passive: true }
        );
      });

      this.syncAutoplay();
    }

    startAutoplay() {
      if (this.autoplayTimer || this.autoplayReleased) return;
      this.autoplayTimer = setInterval(() => this.advance(), this.autoplayDelay);
    }

    stopAutoplay() {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    /** One view along, back to the first card once the end is reached. */
    advance() {
      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      if (maxScroll <= 1) return;

      // Right to left scrolls negative, and sub pixel widths never land exactly.
      if (Math.abs(this.list.scrollLeft) >= maxScroll - 1) {
        this.list.scrollTo({ left: 0 });
        return;
      }

      this.page(1);
    }

    updateArrows() {
      if (!this.previousButton || !this.nextButton) return;

      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      // Right to left scrolls negative, and sub pixel widths never land exactly.
      const position = Math.abs(this.list.scrollLeft);
      const isScrollable = maxScroll > 1;

      this.controls?.toggleAttribute('hidden', !isScrollable);
      this.previousButton.disabled = position <= 1;
      this.nextButton.disabled = position >= maxScroll - 1;
    }
  }

  customElements.define('related-grid', RelatedGrid);
}
